import React, { useState, useEffect } from 'react';
import { getUserInfo } from '@/models/User';
import { useStore } from '@/store';
import { updateCache } from '@/store/action';
import { showMessage, login, showToast } from '@/lib';
import { $api } from '@/queryClient';
import Modal from 'react-modal';
import config from '@/config';
import lang from '@/lib/lng';
import type { UpdateEmail, UpdateEmailRequest } from '@/@types/apiType';
const lng: { [key: string]: string } = lang[config?.lng];

Modal.setAppElement('#root');

type Props = {
    modalStatus: boolean
    closeModal: () => void
}

const ModalUpdateEmail: React.FC<Props> = ({ modalStatus, closeModal }) => {
    const { mutate: updateEmail } = $api.useMutation('post', '/api/user/update-student-email-address');

    const userInfo = getUserInfo();
    const currentStudentEmail = userInfo?.payload?.user?.student_email;
    const [email, setEmail] = useState('');
    const { updateState, isCallingApi } = useStore();

    useEffect(() => {
        if (currentStudentEmail) {
            setEmail(currentStudentEmail);
        }
    }, [currentStudentEmail]);

    const afterOpenModal = () => { };

    const selfClose = (event: React.SyntheticEvent) => {
        event.preventDefault();
        closeModal();
    };

    const handleUpdateEmail = (e: React.SyntheticEvent) => {
        e.preventDefault();

        // Step 1: Event validation
        if (!userInfo?.payload?.event_started) {
            showMessage(config.eventMsg);
            return false;
        }

        // Step 2: User authentication
        if (!userInfo?.payload?.user?.id) {
            login();
            return false;
        }

        // Step 3: Email validation
        if (!email || email.trim().length === 0) {
            showToast('Vui lòng nhập email');
            return false;
        }

        // Step 3.1: Check if new email is same as current email
        if (email.trim() === currentStudentEmail) {
            showToast('Email mới không được trùng với email hiện tại');
            return false;
        }

        // Step 4: Prevent multiple calls
        if (isCallingApi) {
            return false;
        }

        // Step 6: Prepare request data
        const requestData: UpdateEmailRequest = { email_address: email.trim() };

        // Step 7: Execute API call
        showMessage('Bạn có chắc chắn muốn cập nhật email không?').then((res) => {
            if (res.value) {
                updateState('isCallingApi', true);
                updateEmail(
                    { body: requestData },
                    {
                        onSuccess: (data: UpdateEmail) => {
                            if (data?.status === 'successful') {
                                const oldCache = { ...userInfo };
                                showMessage('Cập nhật email thành công.').then(() => {
                                    updateCache(['get', '/api/user/get', {}], null, {
                                        ...oldCache,
                                        payload: {
                                            ...oldCache?.payload,
                                            user: {
                                                ...oldCache?.payload?.user,
                                                student_email: data?.payload?.student_email ?? email.trim(),
                                            },
                                        },
                                    });
                                    updateState('isCallingApi', false);
                                    setEmail('');
                                    closeModal();
                                });
                            } else {
                                const errCode: string = (data as any)?.error_code ?? 'defaultError';
                                showMessage(
                                    lng[errCode] ? lng[errCode] : `${lng.defaultError} [${errCode}]`
                                ).then(() => {
                                    updateState('isCallingApi', false);
                                });
                            }
                        },
                        onError: () => {
                            updateState('isCallingApi', false);
                        },
                    }
                );
            }
        });
    };

    return (
        <Modal
            isOpen={modalStatus}
            onAfterOpen={afterOpenModal}
            onRequestClose={selfClose}
            contentLabel="Example Modal"
            portalClassName="ReactModalPortal"
            overlayClassName=""
            className="animated fadeInDown faster modal-update-email"
        >
            <div className="modal-description">
                <h2>Cập nhật email</h2>
                <div className='verify-student'>
                    <div className="verify-student__form">
                        <div className="verify-student__form__item">
                            <input
                                type="text"
                                placeholder="Nhập email"
                                value={email}
                                className='update-email'
                                onChange={e => setEmail(e.target.value)}
                            />
                        </div>
                        <div className="verify-student__form__actions">
                            <a href="#"
                                className="btn"
                                onClick={e => handleUpdateEmail(e)}
                            >
                                {isCallingApi ? 'Xác nhận' : 'Xác nhận'}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
            <a onClick={event => selfClose(event)} className="close" data-dismiss="modal" aria-label="Close">
                ×
            </a>
        </Modal>
    )
}

export default ModalUpdateEmail